// WDC Global Authentication Library
// Head admin: emirperla96@gmail.com only

import { User, UserRole, Permission, SystemConfig } from '../types';

const HEAD_ADMIN_EMAIL = 'emirperla96@gmail.com';

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  head_admin: [
    { resource: '*', actions: ['read', 'write', 'delete', 'admin'] },
  ],
  admin: [
    { resource: 'users', actions: ['read', 'write'] },
    { resource: 'products', actions: ['read', 'write'] },
    { resource: 'workers', actions: ['read', 'write', 'delete'] },
    { resource: 'profit_loops', actions: ['read', 'write', 'delete'] },
    { resource: 'billing', actions: ['read', 'write'] },
    { resource: 'analytics', actions: ['read'] },
    { resource: 'audit_logs', actions: ['read'] },
  ],
  manager: [
    { resource: 'products', actions: ['read', 'write'] },
    { resource: 'workers', actions: ['read', 'write'] },
    { resource: 'profit_loops', actions: ['read', 'write'] },
    { resource: 'analytics', actions: ['read'] },
  ],
  analyst: [
    { resource: 'products', actions: ['read'] },
    { resource: 'workers', actions: ['read'] },
    { resource: 'profit_loops', actions: ['read'] },
    { resource: 'analytics', actions: ['read'] },
  ],
  viewer: [
    { resource: 'products', actions: ['read'] },
    { resource: 'analytics', actions: ['read'] },
  ],
};

export function isHeadAdmin(email: string): boolean {
  return email.toLowerCase() === HEAD_ADMIN_EMAIL.toLowerCase();
}

export function getUserRole(email: string, existingRole?: UserRole): UserRole {
  if (isHeadAdmin(email)) return 'head_admin';
  return existingRole || 'viewer';
}

export function hasPermission(user: User, resource: string, action: string): boolean {
  // Head admin has all permissions
  if (user.role === 'head_admin') return true;

  const permissions = ROLE_PERMISSIONS[user.role] || [];
  
  for (const perm of permissions) {
    if ((perm.resource === '*' || perm.resource === resource) && 
        perm.actions.includes(action as any)) {
      return true;
    }
  }
  return false;
}

export function createHeadAdminUser(): User {
  return {
    id: 'head-admin-001',
    email: HEAD_ADMIN_EMAIL,
    name: 'Emir Perla',
    role: 'head_admin',
    createdAt: new Date(),
    isActive: true,
    permissions: ROLE_PERMISSIONS.head_admin,
    subscriptionTier: 'enterprise',
  };
}

export function validateSession(session: any): User | null {
  if (!session?.user?.email) return null;
  
  const email = session.user.email.toLowerCase();
  const role = getUserRole(email, session.user.role);
  
  return {
    id: session.user.id || `user-${Date.now()}`,
    email,
    name: session.user.name || email.split('@')[0],
    role,
    avatar: session.user.avatar,
    createdAt: new Date(session.user.createdAt || Date.now()),
    lastLoginAt: new Date(),
    isActive: true,
    permissions: ROLE_PERMISSIONS[role],
    stripeCustomerId: session.user.stripeCustomerId,
    subscriptionTier: session.user.subscriptionTier || 'free',
  };
}

export const SYSTEM_CONFIG: SystemConfig = {
  headAdminEmail: HEAD_ADMIN_EMAIL,
  allowedDomains: ['gmail.com', 'outlook.com', 'yahoo.com', 'proton.me'],
  maintenanceMode: false,
  features: {
    aiWorkersEnabled: true,
    profitLoopsEnabled: true,
    multiProductAccess: true,
    advancedAnalytics: true,
    apiAccess: true,
    whiteLabel: false,
  },
  limits: {
    maxWorkersPerUser: 50,
    maxProfitLoopsPerUser: 20,
    maxApiCallsPerMinute: 1000,
    maxStorageGb: 100,
  },
};

export function getSystemConfig(): SystemConfig {
  return SYSTEM_CONFIG;
}